import {
  clearError,
  el,
  formatBytes,
  getFramePlan,
  getSettings,
  revokeResult,
  sanitizeFilename,
  showError,
  state,
} from "./core.js"
import { closeImageMap, decodeFrameImages } from "./media.js"
import { inspectAnimatedWebp, muxAnimatedWebp } from "./webp-mux.js"
import {
  getCropDecodeDimensions,
  getCropRectForDecoded,
  getCroppedOutputDimensions,
} from "./crop.js"
import { getWorkerDecision, updateLiveWorkerStatus } from "./workers.js"

const GIF_CODEC_URLS = [
  "https://esm.sh/gifenc@1.0.3/src/index.js?exports=GIFEncoder",
  "https://cdn.jsdelivr.net/npm/gifenc@1.0.3/+esm",
]
let gifCodecPromise = null

async function loadGifEncoder() {
  if (globalThis.__GIF_ENCODER_TEST_FACTORY__) return globalThis.__GIF_ENCODER_TEST_FACTORY__
  if (gifCodecPromise) return gifCodecPromise
  gifCodecPromise = (async () => {
    const errors = []
    for (const url of GIF_CODEC_URLS) {
      try {
        const module = await import(url)
        if (typeof module.GIFEncoder === "function") return module.GIFEncoder
      } catch (error) {
        errors.push(error instanceof Error ? error.message : String(error))
      }
    }
    throw new Error(`Could not load the GIF encoder. ${errors.join(" | ")}`)
  })().catch((error) => {
    gifCodecPromise = null
    throw error
  })
  return gifCodecPromise
}

function median(values) {
  if (!values.length) return 0
  const sorted = values.slice().sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

class AdaptiveWorkerPool {
  constructor(options) {
    this.url = options.url
    this.manual = options.manual
    this.min = options.manual ? options.initial : Math.max(1, options.min)
    this.max = options.manual ? options.initial : Math.max(options.initial, options.max)
    this.queue = []
    this.entries = []
    this.nextId = 1
    this.closed = false
    this.completedTimes = []
    this.latencies = []
    this.bestLatency = Infinity
    this.lastScaleAt = performance.now()
    this.lastReason = options.manual ? "Manual worker count is fixed during conversion." : "Auto is measuring queue pressure and throughput."
    this.onChange = options.onChange || (() => {})
    for (let index = 0; index < options.initial; index += 1) this.addWorker(false)
    this.timer = options.manual ? 0 : setInterval(() => this.adapt(), 1200)
    this.report()
  }

  get count() {
    return this.entries.filter((entry) => !entry.retiring).length
  }

  get busyCount() {
    return this.entries.filter((entry) => entry.task).length
  }

  get throughput() {
    const now = performance.now()
    this.completedTimes = this.completedTimes.filter((time) => now - time <= 5000)
    if (this.completedTimes.length < 2) return 0
    const seconds = Math.max(0.25, (this.completedTimes.at(-1) - this.completedTimes[0]) / 1000)
    return (this.completedTimes.length - 1) / seconds
  }

  addWorker(report = true) {
    if (this.closed || this.count >= this.max) return false
    const worker = new Worker(this.url)
    const entry = { worker, task: null, retiring: false }
    worker.onmessage = (event) => {
      const task = entry.task
      entry.task = null
      if (!task) return
      const latency = Math.max(1, performance.now() - task.startedAt)
      this.latencies.push(latency)
      if (this.latencies.length > 24) this.latencies.shift()
      this.bestLatency = Math.min(this.bestLatency, median(this.latencies))
      this.completedTimes.push(performance.now())
      if (event.data?.ok) task.resolve(event.data)
      else task.reject(new Error(event.data?.error || "Frame worker failed."))
      if (entry.retiring) this.destroyEntry(entry)
      this.dispatch()
      this.report()
    }
    worker.onerror = (event) => {
      const error = new Error(event.message || "Frame worker failed to load.")
      entry.task?.reject(error)
      entry.task = null
      this.lastReason = "A worker failed; conversion stopped to avoid corrupt output."
      for (const task of this.queue.splice(0)) task.reject(error)
      this.terminate()
    }
    this.entries.push(entry)
    this.dispatch()
    if (report) this.report()
    return true
  }

  retireWorker(reason) {
    if (this.closed || this.count <= this.min) return false
    const entry = [...this.entries].reverse().find((candidate) => !candidate.retiring && !candidate.task)
      || [...this.entries].reverse().find((candidate) => !candidate.retiring)
    if (!entry) return false
    entry.retiring = true
    this.lastReason = reason
    if (!entry.task) this.destroyEntry(entry)
    this.report()
    return true
  }

  destroyEntry(entry) {
    entry.worker.terminate()
    const index = this.entries.indexOf(entry)
    if (index >= 0) this.entries.splice(index, 1)
  }

  run(payload, transfer = []) {
    if (this.closed) return Promise.reject(new Error("Worker pool is closed."))
    return new Promise((resolve, reject) => {
      this.queue.push({ id: this.nextId++, payload, transfer, resolve, reject, startedAt: 0 })
      this.dispatch()
    })
  }

  dispatch() {
    for (const entry of this.entries) {
      if (entry.retiring || entry.task || !this.queue.length) continue
      const task = this.queue.shift()
      task.startedAt = performance.now()
      entry.task = task
      entry.worker.postMessage({ id: task.id, payload: task.payload }, task.transfer)
    }
  }

  memoryPressure() {
    const memory = performance.memory
    if (!memory?.jsHeapSizeLimit || !memory?.usedJSHeapSize) return 0
    return memory.usedJSHeapSize / memory.jsHeapSizeLimit
  }

  adapt() {
    if (this.closed || this.manual) return
    const now = performance.now()
    const active = Math.max(1, this.count)
    const busyRatio = this.busyCount / active
    const queuePressure = this.queue.length / active
    const latency = median(this.latencies.slice(-10))
    const memory = this.memoryPressure()
    const sinceScale = now - this.lastScaleAt

    if (memory > 0.84 && active > this.min && sinceScale > 900) {
      if (this.retireWorker(`Auto reduced workers because browser heap use reached ${Math.round(memory * 100)}%.`)) this.lastScaleAt = now
      return
    }

    if (latency && this.bestLatency < Infinity && latency > this.bestLatency * 1.75 && active > this.min && sinceScale > 1800) {
      if (this.retireWorker("Auto reduced workers because per-frame latency rose sharply, indicating contention.")) this.lastScaleAt = now
      return
    }

    if (queuePressure > 1.25 && busyRatio >= 0.8 && active < this.max && sinceScale > 1100) {
      this.lastReason = `Auto added a worker because ${this.queue.length} frame jobs were queued and ${Math.round(busyRatio * 100)}% of workers were busy.`
      if (this.addWorker(false)) this.lastScaleAt = now
      this.report()
      return
    }

    if (!this.queue.length && busyRatio < 0.3 && active > this.min && sinceScale > 2400) {
      if (this.retireWorker("Auto removed an idle worker because the queue drained.")) this.lastScaleAt = now
      return
    }

    this.lastReason = this.queue.length
      ? `Auto is holding ${active} workers: ${this.queue.length} queued, ${Math.round(busyRatio * 100)}% busy.`
      : `Auto is holding ${active} workers while the current batch finishes.`
    this.report()
  }

  report() {
    this.onChange({
      count: Math.max(0, this.count),
      max: this.max,
      manual: this.manual,
      reason: this.lastReason,
      throughput: this.throughput,
    })
  }

  terminate() {
    if (this.closed) return
    this.closed = true
    if (this.timer) clearInterval(this.timer)
    for (const task of this.queue.splice(0)) task.reject(new DOMException("Conversion cancelled.", "AbortError"))
    for (const entry of this.entries.splice(0)) {
      entry.task?.reject(new DOMException("Conversion cancelled.", "AbortError"))
      entry.task = null
      entry.worker.terminate()
    }
  }
}

class StableConversionProgress {
  constructor(format, totals) {
    this.format = format
    this.totals = totals
    this.startedAt = performance.now()
    this.lastRenderAt = 0
    this.lastEtaAt = this.startedAt
    this.lastFraction = 0
    this.displayedEta = null
    this.currentKey = null
    this.phase = null
    this.phaseSamples = []
    this.profile = this.loadProfile()
    this.meta = document.getElementById("progressMeta")
    this.phaseElement = document.getElementById("progressPhase")
    this.summary = document.getElementById("progressSummary")
    this.ranges = format === "gif"
      ? { decode: [0.01, 0.18], process: [0.18, 0.62], assemble: [0.62, 0.97], verify: [0.97, 1] }
      : { decode: [0.01, 0.16], process: [0.16, 0.90], assemble: [0.90, 0.97], verify: [0.97, 1] }
    el.progress.hidden = false
    if (this.meta) this.meta.hidden = false
    this.render(0.01, "Preparing conversion", "ETA: measuring…", true)
  }

  loadProfile() {
    try {
      return JSON.parse(localStorage.getItem("videoConverterEta") || "{}")
    } catch {
      return {}
    }
  }

  saveProfile(key, secondsPerUnit) {
    if (!Number.isFinite(secondsPerUnit) || secondsPerUnit <= 0) return
    const previous = Number(this.profile[key]) || secondsPerUnit
    this.profile[key] = previous * 0.7 + secondsPerUnit * 0.3
    try {
      localStorage.setItem("videoConverterEta", JSON.stringify(this.profile))
    } catch {
      // Storage can be disabled.
    }
  }

  beginPhase(key) {
    if (this.currentKey === key) return
    if (this.phase?.done > 0 && this.phaseSamples.length >= 3) this.saveProfile(this.currentKey, median(this.phaseSamples))
    this.currentKey = key
    this.phase = { startedAt: performance.now(), lastAt: performance.now(), lastDone: 0, done: 0 }
    this.phaseSamples = []
  }

  estimateRemaining(key, done, total) {
    const samples = this.phaseSamples.slice(-9)
    const currentUnit = samples.length >= 3 ? median(samples) : Number(this.profile[key]) || 0
    if (!currentUnit) return null
    let seconds = Math.max(0, total - done) * currentUnit
    const keys = Object.keys(this.ranges)
    const currentIndex = keys.indexOf(key)
    const [start, end] = this.ranges[key]
    const currentWeight = Math.max(0.001, end - start)
    const currentPhaseTotal = currentUnit * total
    for (let index = currentIndex + 1; index < keys.length; index += 1) {
      const futureKey = keys[index]
      const futureTotal = this.totals[futureKey] || 1
      const profiledUnit = Number(this.profile[futureKey]) || 0
      if (profiledUnit) seconds += profiledUnit * futureTotal
      else {
        const [futureStart, futureEnd] = this.ranges[futureKey]
        seconds += currentPhaseTotal / currentWeight * (futureEnd - futureStart)
      }
    }
    return seconds
  }

  smoothEta(raw, now) {
    if (!Number.isFinite(raw)) return this.displayedEta
    if (this.displayedEta == null) {
      this.displayedEta = raw
      this.lastEtaAt = now
      return raw
    }
    const elapsed = Math.max(0.1, (now - this.lastEtaAt) / 1000)
    const baseline = Math.max(0, this.displayedEta - elapsed)
    const lower = Math.max(0, baseline * Math.pow(0.88, elapsed) - 0.5)
    const upper = baseline * Math.pow(1.12, elapsed) + 1
    this.displayedEta = Math.min(upper, Math.max(lower, raw))
    this.lastEtaAt = now
    return this.displayedEta
  }

  static formatEta(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return "ETA: measuring…"
    if (seconds < 1) return "ETA: <1s"
    if (seconds < 60) return `ETA: ~${Math.ceil(seconds)}s`
    const minutes = Math.floor(seconds / 60)
    const remainder = Math.ceil(seconds % 60)
    return `ETA: ~${minutes}m ${String(remainder).padStart(2, "0")}s`
  }

  update(key, done, total, label) {
    this.beginPhase(key)
    const now = performance.now()
    const safeDone = Math.min(Number(done) || 0, Math.max(1, Number(total) || 1))
    if (safeDone > this.phase.lastDone) {
      const deltaDone = safeDone - this.phase.lastDone
      const secondsPerUnit = (now - this.phase.lastAt) / 1000 / deltaDone
      if (secondsPerUnit > 0.0001 && secondsPerUnit < 120) {
        this.phaseSamples.push(secondsPerUnit)
        if (this.phaseSamples.length > 15) this.phaseSamples.shift()
      }
      this.phase.lastDone = safeDone
      this.phase.lastAt = now
      this.phase.done = safeDone
    }

    const [start, end] = this.ranges[key]
    const ratio = safeDone / Math.max(1, total)
    const fraction = Math.max(this.lastFraction, start + (end - start) * ratio)
    const enoughEvidence = this.phaseSamples.length >= 3 || Number(this.profile[key]) > 0
    const rawEta = enoughEvidence ? this.estimateRemaining(key, safeDone, total) : null
    const eta = rawEta == null ? this.displayedEta : this.smoothEta(rawEta, now)
    const etaText = enoughEvidence || this.displayedEta != null ? StableConversionProgress.formatEta(eta) : "ETA: measuring…"
    this.render(fraction, `${label} ${safeDone}/${total}`, etaText, now - this.lastRenderAt >= 500)
  }

  message(key, label) {
    this.beginPhase(key)
    const [start] = this.ranges[key]
    this.render(Math.max(this.lastFraction, start), label, this.displayedEta == null ? "ETA: measuring…" : StableConversionProgress.formatEta(this.displayedEta), true)
  }

  render(fraction, label, eta, updateText) {
    const safe = Math.min(1, Math.max(this.lastFraction, fraction))
    this.lastFraction = safe
    const percent = Math.floor(safe * 100)
    el.progressBar.style.width = `${safe * 100}%`
    el.progress.setAttribute("aria-valuenow", String(percent))
    if (!updateText) return
    this.lastRenderAt = performance.now()
    if (this.phaseElement) this.phaseElement.textContent = label
    if (this.summary) this.summary.textContent = `${percent}% · ${eta}`
    el.statusText.textContent = `${label} · ${percent}% · ${eta}`
  }

  complete() {
    if (this.phase?.done > 0 && this.phaseSamples.length >= 3) this.saveProfile(this.currentKey, median(this.phaseSamples))
    this.displayedEta = 0
    this.render(1, "Complete", "ETA: 0s", true)
  }

  stop(label) {
    if (this.phaseElement) this.phaseElement.textContent = label
    if (this.summary) this.summary.textContent = ""
  }
}

function gifBytes(gif) {
  if (typeof gif.bytesView === "function") return gif.bytesView()
  const bytes = gif.bytes?.()
  if (bytes) return bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  throw new Error("The GIF encoder did not return output bytes.")
}

async function cloneDrawable(image, dimensions) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(image)
      return { bitmap, transfer: [bitmap] }
    } catch {
      // RGBA fallback below.
    }
  }
  const canvas = image instanceof HTMLCanvasElement ? image : document.createElement("canvas")
  if (!(image instanceof HTMLCanvasElement)) {
    canvas.width = dimensions.width
    canvas.height = dimensions.height
    canvas.getContext("2d", { alpha: false }).drawImage(image, 0, 0, dimensions.width, dimensions.height)
  }
  const rgba = canvas.getContext("2d", { alpha: false, willReadFrequently: true })
    .getImageData(0, 0, dimensions.width, dimensions.height).data.buffer
  return { rgba, transfer: [rgba] }
}

async function buildWorkerPayload(spec, images, outputDimensions, decodeDimensions, crop, settings) {
  const fromImage = images.get(spec.from)
  const toImage = images.get(spec.to)
  if (!fromImage || !toImage) throw new Error(`Missing decoded source frame ${spec.from + 1}.`)
  const first = await cloneDrawable(fromImage, decodeDimensions)
  const payload = {
    width: outputDimensions.width,
    height: outputDimensions.height,
    sourceWidth: decodeDimensions.width,
    sourceHeight: decodeDimensions.height,
    crop,
    mix: spec.mix,
    format: settings.format,
    settings,
  }
  const transfer = [...first.transfer]
  if (first.bitmap) payload.bitmapA = first.bitmap
  else payload.rgbaA = first.rgba
  if (spec.mix > 0 && spec.to !== spec.from) {
    const second = await cloneDrawable(toImage, decodeDimensions)
    transfer.push(...second.transfer)
    if (second.bitmap) payload.bitmapB = second.bitmap
    else payload.rgbaB = second.rgba
  }
  return { payload, transfer }
}

function canonicalFrameKey(spec, motionBlur = 0) {
  if (!spec) return "missing"
  const blur = Math.round(Number(motionBlur) || 0)
  if (spec.baseFrame) return `base:${spec.from}:${spec.to}:blur:${blur}`
  let from = Number(spec.from)
  let to = Number(spec.to)
  let mix = Math.round((Number(spec.mix) || 0) * 1e8) / 1e8
  if (from > to) {
    ;[from, to] = [to, from]
    mix = Math.round((1 - mix) * 1e8) / 1e8
  }
  return `${from}:${to}:${mix}:blur:${blur}`
}

async function processOutputFrames(pool, images, plan, outputDimensions, decodeDimensions, settings, progress) {
  const results = new Array(plan.frames.length)
  const crop = getCropRectForDecoded(decodeDimensions)
  const jobsByKey = new Map()

  plan.frames.forEach((spec, index) => {
    const key = canonicalFrameKey(spec, settings.motionBlur)
    const existing = jobsByKey.get(key)
    if (existing) existing.indices.push(index)
    else jobsByKey.set(key, { key, spec, indices: [index] })
  })

  const jobs = [...jobsByKey.values()]
  dispatchEvent(new CustomEvent("converter-frame-cache", {
    detail: {
      output: plan.frames.length,
      unique: jobs.length,
      reused: plan.frames.length - jobs.length,
    },
  }))

  const active = new Set()
  let completed = 0
  for (const job of jobs) {
    if (state.cancelRequested) throw new DOMException("Conversion cancelled.", "AbortError")
    const prepared = await buildWorkerPayload(job.spec, images, outputDimensions, decodeDimensions, crop, settings)
    const promise = pool.run(prepared.payload, prepared.transfer)
      .then((result) => {
        for (const index of job.indices) results[index] = result
        completed += job.indices.length
        progress.update("process", completed, plan.frames.length, settings.format === "gif" ? "Phase 2/4 · Quantizing" : "Phase 2/4 · Encoding WebP frames")
      })
      .finally(() => active.delete(promise))
    active.add(promise)
    const queueWindow = Math.max(2, pool.count * 3)
    if (active.size >= queueWindow) await Promise.race(active)
  }
  await Promise.all(active)
  if (results.some((result) => !result)) throw new Error("Not all output frames were processed.")
  return results
}

async function encodeGif(results, plan, dimensions, progress) {
  progress.message("assemble", "Phase 3/4 · Loading GIF assembler")
  const GIFEncoder = await loadGifEncoder()
  const gif = GIFEncoder()
  for (let index = 0; index < results.length; index += 1) {
    if (state.cancelRequested) throw new DOMException("Conversion cancelled.", "AbortError")
    const result = results[index]
    gif.writeFrame(new Uint8Array(result.indexed), dimensions.width, dimensions.height, {
      palette: result.palette,
      delay: plan.delayMs,
      repeat: 0,
    })
    progress.update("assemble", index + 1, results.length, "Phase 3/4 · Compressing GIF")
  }
  gif.finish()
  return new Blob([gifBytes(gif)], { type: "image/gif" })
}

function canvasToBlob(canvas, options) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Browser WebP encoder returned no frame.")), options.type, options.quality)
  })
}

async function encodeStillWebpFallback(rgbaBuffer, dimensions, quality) {
  const canvas = document.createElement("canvas")
  canvas.width = dimensions.width
  canvas.height = dimensions.height
  const context = canvas.getContext("2d", { alpha: false })
  context.putImageData(new ImageData(new Uint8ClampedArray(rgbaBuffer), dimensions.width, dimensions.height), 0, 0)
  const blob = await canvasToBlob(canvas, { type: "image/webp", quality: quality / 100 })
  if (blob.type !== "image/webp") throw new Error("This browser cannot encode WebP images.")
  return blob.arrayBuffer()
}

async function encodeAnimatedWebp(results, plan, dimensions, settings, progress) {
  const frames = []
  for (let index = 0; index < results.length; index += 1) {
    if (state.cancelRequested) throw new DOMException("Conversion cancelled.", "AbortError")
    const result = results[index]
    const webp = result.webp || await encodeStillWebpFallback(result.rgba, dimensions, settings.webpQuality)
    frames.push({ webp, duration: plan.delayMs })
    progress.update("assemble", index + 1, results.length + 1, "Phase 3/4 · Preparing WebP animation")
  }
  const bytes = muxAnimatedWebp(dimensions.width, dimensions.height, frames)
  progress.update("assemble", results.length + 1, results.length + 1, "Phase 3/4 · Muxing WebP")
  const inspection = inspectAnimatedWebp(bytes)
  if (!inspection.valid || inspection.frames !== results.length || inspection.loopCount !== 0) {
    throw new Error("Animated WebP mux validation failed.")
  }
  return new Blob([bytes], { type: "image/webp" })
}

function enforceMemoryBudget(uniqueCount, plan, outputDimensions, decodeDimensions, workerCount) {
  const decodedPixels = decodeDimensions.width * decodeDimensions.height
  const outputPixels = outputDimensions.width * outputDimensions.height
  const decodedBytes = decodedPixels * 4 * uniqueCount
  const activeBytes = (decodedPixels + outputPixels) * 4 * workerCount * 4
  const indexedBytes = state.format === "gif" ? outputPixels * plan.totalFrames : 0
  const required = decodedBytes + activeBytes + indexedBytes
  const deviceBytes = Math.max(1, Number(navigator.deviceMemory) || 4) * 1024 ** 3
  const budget = Math.max(192 * 1024 ** 2, Math.min(1.5 * 1024 ** 3, deviceBytes * 0.32))
  if (required > budget) {
    throw new Error(`This conversion needs about ${formatBytes(required)} of working memory. Reduce resolution, FPS, morphing, crop size, worker count, or enable Maximum frames.`)
  }
}

async function verifyImageBlob(blob) {
  const url = URL.createObjectURL(blob)
  try {
    const image = new Image()
    image.src = url
    if (typeof image.decode === "function") await image.decode()
    else await new Promise((resolve, reject) => {
      image.onload = resolve
      image.onerror = () => reject(new Error("The generated image could not be decoded."))
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

export async function encodeOutput() {
  if (!state.file || !state.videoTrack || state.encoding) return
  clearError()
  revokeResult(true)
  state.encoding = true
  state.cancelRequested = false
  el.convertButton.disabled = true
  el.cancelButton.hidden = false
  el.progressBar.style.width = "0%"

  let pool = null
  let progress = null
  let images = null
  let succeeded = false

  try {
    const settings = getSettings()
    const outputDimensions = getCroppedOutputDimensions(settings)
    const decodeDimensions = getCropDecodeDimensions(outputDimensions)
    const plan = getFramePlan(settings)
    const uniqueIndices = [...new Set(plan.frames.flatMap((frame) => [frame.from, frame.to]))].sort((a, b) => a - b)
    const decision = getWorkerDecision(outputDimensions, plan.totalFrames, settings)
    const assembleTotal = settings.format === "webp" ? plan.frames.length + 1 : plan.frames.length
    progress = new StableConversionProgress(settings.format, {
      decode: uniqueIndices.length,
      process: plan.frames.length,
      assemble: assembleTotal,
      verify: 1,
    })
    enforceMemoryBudget(uniqueIndices.length, plan, outputDimensions, decodeDimensions, decision.safeMax)
    pool = new AdaptiveWorkerPool({
      initial: decision.startCount,
      min: decision.minCount,
      max: decision.safeMax,
      manual: decision.manual,
      url: new URL("../frame-worker.js", import.meta.url),
      onChange: updateLiveWorkerStatus,
    })

    images = await decodeFrameImages(uniqueIndices, decodeDimensions, {
      isCancelled: () => state.cancelRequested,
      onProgress: (done, total) => progress.update("decode", done, total, "Phase 1/4 · Decoding source frames"),
    })
    const results = await processOutputFrames(pool, images, plan, outputDimensions, decodeDimensions, settings, progress)
    closeImageMap(images)
    images = null

    const blob = settings.format === "gif"
      ? await encodeGif(results, plan, outputDimensions, progress)
      : await encodeAnimatedWebp(results, plan, outputDimensions, settings, progress)
    if (state.cancelRequested) throw new DOMException("Conversion cancelled.", "AbortError")

    progress.update("verify", 0, 1, "Phase 4/4 · Verifying output")
    await verifyImageBlob(blob)
    progress.update("verify", 1, 1, "Phase 4/4 · Verified")

    state.resultUrl = URL.createObjectURL(blob)
    el.resultPreview.removeAttribute("src")
    el.resultPreview.src = state.resultUrl
    el.resultPreview.hidden = false
    el.livePreview.hidden = true
    el.emptyResult.hidden = true
    el.previewBadge.textContent = `${settings.format.toUpperCase()} result · looping`
    const extension = settings.format === "gif" ? "gif" : "webp"
    el.downloadButton.href = state.resultUrl
    el.downloadButton.download = `${sanitizeFilename(state.file.name)}.${extension}`
    el.downloadButton.classList.remove("disabled")
    el.downloadButton.setAttribute("aria-disabled", "false")
    progress.complete()
    el.statusText.textContent = `Done: ${plan.totalFrames} frames · ${formatBytes(blob.size)} · ${decision.manual ? "fixed" : "adaptive"} workers.`
    succeeded = true
  } catch (error) {
    if (error?.name === "AbortError") {
      progress?.stop("Cancelled")
      el.statusText.textContent = "Conversion cancelled."
    } else {
      progress?.stop("Failed")
      showError(error)
    }
  } finally {
    closeImageMap(images)
    pool?.terminate()
    state.encoding = false
    state.cancelRequested = false
    el.cancelButton.hidden = true
    el.convertButton.disabled = !state.file || !state.videoTrack
    if (!succeeded) setTimeout(() => {
      el.progress.hidden = true
      const meta = document.getElementById("progressMeta")
      if (meta) meta.hidden = true
    }, 900)
  }
}
